import React, { useState } from "react";
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button, Skeleton, Tooltip } from "@/components/ui";
import { ReviewServices } from "@/services/bedrock";
import { cn } from "@/lib/utils";

interface ReviewInboxListFooterProps {
  playerId: number | null;
  totalCount: number;
  isLoading: boolean;
}

export function ReviewInboxListFooter({ playerId, totalCount, isLoading }: ReviewInboxListFooterProps) {
  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSync = async () => {
    if (!playerId || isSyncing) return;

    setIsSyncing(true);
    try {
      const { syncingNetworks, recentlySyncedNetworks } = await ReviewServices.syncPlayerReviews(playerId);

      if (syncingNetworks.length === 0 && recentlySyncedNetworks.length === 0) {
        toast.info("No review sources to sync", {
          description: "Connect a Facebook, Google, or Zillow profile with review sync enabled.",
        });
        return;
      }

      if (syncingNetworks.length === 0) {
        toast.info("Recently synced", {
          description: "Your connected sources were synced recently. Try again in a few minutes.",
        });
        return;
      }

      toast.success("Sync in progress!", {
        description:
          recentlySyncedNetworks.length > 0
            ? "Some sources were synced recently and were skipped. Others will update in a few minutes."
            : "Your reviews will update in a few minutes.",
      });
      window.setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["reviews-inbox-list-infinite"] }).catch(console.error);
      }, 15_000);
    } catch (error) {
      console.error("Failed to sync reviews:", error);
      toast.error(typeof error === "string" ? error : "Failed to request sync. Please try again later.");
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="flex items-center gap-1 px-3 py-1.5">
      {isLoading ? (
        <Skeleton className="h-3 w-16 rounded" />
      ) : (
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 tabular-nums">
          <span className="text-slate-600">{totalCount}</span> {totalCount === 1 ? "review" : "reviews"}
        </span>
      )}

      <Tooltip>
        <Tooltip.Trigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={handleSync}
              disabled={!playerId || isSyncing}
              aria-label={isSyncing ? "Syncing reviews" : "Sync reviews"}
              className={cn(
                "shrink-0 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50/70",
                isSyncing && "text-indigo-500"
              )}
            >
              <RefreshCw className={cn("size-3.5", isSyncing && "animate-spin")} />
            </Button>
          }
        />
        <Tooltip.Content className="font-sans">Sync latest reviews from connected sources</Tooltip.Content>
      </Tooltip>
    </div>
  );
}
